import { Box, Typography } from "@mui/material";

import type { AvailabilityGridColumn } from "./AvailabilityGrid.js";
import type { AvailabilityBlock } from "./availabilityGridModel.js";
import { summariseAvailabilityByColumn } from "./availabilityGridSummaryLines.js";

export interface AvailabilityGridSummaryProps {
  /** The grid's columns, so lines carry the same labels as the headings. */
  columns: readonly AvailabilityGridColumn[];
  value: readonly AvailabilityBlock[];
  "data-testid"?: string;
}

/**
 * The availability a grid shows, as text: one line per column with any, see
 * {@link summariseAvailabilityByColumn}. Each part of a line is a grid column,
 * so days, times and counts line up without a monospace font. Renders nothing
 * for an empty grid.
 */
export function AvailabilityGridSummary({
  columns,
  value,
  "data-testid": dataTestId = "availability-grid-summary",
}: AvailabilityGridSummaryProps) {
  const lines = summariseAvailabilityByColumn(columns, value);
  if (lines.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(5, max-content)",
        columnGap: 1,
      }}
    >
      {lines.map((line) => (
        <Typography
          key={line.key}
          variant="body2"
          component="div"
          data-testid={`${dataTestId}-${line.key}`}
          sx={{ display: "contents" }}
        >
          <span>{line.label}</span>
          <span>{line.start}</span>
          <span>-</span>
          <span>{line.end}</span>
          <span>{line.slotCount > 1 ? `(${String(line.slotCount)})` : ""}</span>
        </Typography>
      ))}
    </Box>
  );
}
